import 'twin.macro';
import React, { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import tw from 'twin.macro';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import Input from 'components/form/input/Input';
import ColorPicker from 'components/form/ColorPicker';
import { Title3 } from 'components/typography/Typography';
import { LogoPlace } from 'types/adEditorTypes';
import {
  LogoButton,
  LogoPlaceButtons,
} from 'components/logo-place/LogoPlaceButtons';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import CampaignValueRows from 'components/ad-builder/CampaignValueRows';
import { ValueField } from 'utils/campaignValues';
import {
  acceptsFieldEdit,
  templateHasAnyField,
  templateLogoChoice,
  templateShowsField,
} from 'utils/templateFieldHelpers';

/**
 * SYM-5852. The six v2 templates introduced fields that nothing could edit: a customer could pick
 * B2 -- the salary and benefits lane -- and had no way to enter a salary or a single benefit, so it
 * rendered whatever happened to be in the database, which for most customers is nothing.
 *
 * Deliberately a PANEL rather than editing on the preview itself. The templates measure text and
 * then shrink and re-wrap it, so a browser-side edit box cannot know the server is about to change
 * the font size; you would type at one size and watch the render settle to another. Avoiding that
 * means reimplementing the fitter in JS, which is a second source of truth for layout -- the exact
 * thing these templates are server-rendered to avoid. Full reasoning on SYM-5852.
 */
const MAX_BENEFITS = 3;

/**
 * The most a field may hold. SYM-5907: a cap now, not a warning.
 *
 * These numbers are unchanged -- they are the lengths past which the renderers stop fitting -- but
 * what happens at them is not. They used to raise "Over 40 characters. This may be shortened in
 * the ad" and let you keep typing, on the stated assumption that the renderer would ellipsise.
 * It did not. The hook line ran straight out of the card (SYM-5914), so the customer was told the
 * ad would cope while the preview visibly broke.
 *
 * Meanwhile the server disagreed in the other direction: benefits warned here at 34 and were hard
 * rejected at 64, and because every tile posts the same values to the preview endpoint, one long
 * benefit answered 422 for all six and blanked the whole picker.
 *
 * So there were three numbers for one field and no two agreed. There is now one rule per layer:
 * the input stops you here, the server accepts a generous ceiling and never refuses what it
 * stored, and the renderer cuts to the box as a last resort for rows saved before this shipped.
 */
const MAX_LENGTHS: Record<string, number> = {
  free_text: 40,
  subtext: 46,
  salary: 32,
  benefit: 34,
  // SYM-5915. The badge is a circle on J2 and J4 and a pill on S1 and J5, and the renderer fits
  // the text to the shape rather than the shape to the text, so a long word comes out tiny before
  // it comes out wrong. Short enough that the customer sees the limit rather than the shrinkage.
  banner_title: 16,
};

export const TemplateValueControls = () => {
  const {
    currentVariant,
    updateCurrentVariant,
    refreshTemplatePreview,
    defaultAdOverlays,
  } = useAdEditorContext();

  // The SAME flag the renderer gates the salary band on (SalaryCapabilityResolver::
  // EXPOSE_SALARY_FLAG), evaluated against the Customer group NavigationContext already sets, so
  // the editor and the server agree on who may show a salary.
  //
  // Shown DISABLED rather than hidden: the capability is a paid one, so a customer who cannot use
  // it should still see that it exists. What must not happen is an input that looks live, saves a
  // value, and changes nothing on the ad -- which is what it did, with only a grey hint to explain.
  //
  // Disabling also closes a residual flagged during the July review gate: `salary` persists for a
  // customer who is not entitled to it, so a value typed today would start appearing by itself if
  // that customer later gained the capability. Nothing could set the field back then. This panel is
  // what made that reachable.
  const canExposeSalary = useFeatureFlagEnabled('expose_salary');

  const template = currentVariant?.template;
  if (!template) return null;

  const benefits = currentVariant?.benefits ?? [];

  const setField = (
    key: 'free_text' | 'subtext' | 'salary' | 'banner_title',
    value: string
  ) => {
    // Empty means "not set", not an empty line: the renderers omit the element entirely for null
    // and would otherwise reserve space for a blank row.
    //
    // SYM-5955: banner_title is the exception, and the exception is the whole fix. The callout is
    // the one field with a BRAND DEFAULT behind it, so for it "" and null have to mean different
    // things: "" is the customer clearing the badge away, null is a variant that never had one and
    // should still show the brand's. Collapsing them here is what made a cleared callout come back
    // as WANTED -- the save layer's `??` was written to preserve the empty string, and never saw
    // one because this line had already turned it into null.
    //
    // The other three have no fallback behind them, so null remains the honest "not set" for them.
    const preserveEmpty = key === 'banner_title';

    updateCurrentVariant(key, value === '' && !preserveEmpty ? null : value);
    refreshTemplatePreview();
  };

  const setBenefits = (next: string[]) => {
    const cleaned = next.filter((b) => b.trim() !== '');
    updateCurrentVariant('benefits', cleaned.length ? cleaned : null);
    refreshTemplatePreview();
  };

  /**
   * SYM-5875. Which fields this template actually draws, declared by the RENDERER and sent by the
   * API alongside the template code.
   *
   * Salary and benefits used to be gated on `template === 'b2'` written here, and the hook and
   * extra lines were not gated at all -- so the editor offered a hook line on all six templates
   * when three draw it, and an extra line on all six when one draws it. Typing into either on S1
   * did nothing at all, with nothing on screen to say why.
   *
   * Asking the server rather than restating the rule here is the point: a list kept in this file is
   * a copy of a fact that lives in the renderer, and it goes stale the first time a template starts
   * or stops drawing something -- silently, because the symptom is a control that quietly does
   * nothing. The same failure as the band heights hand-computed from the safe zone in J1 and B2.
   */
  const declared = defaultAdOverlays.find(
    (overlay) => overlay.template_code === template
  )?.content_fields;

  const showHook = templateShowsField(declared, template, 'free_text');
  const showExtra = templateShowsField(declared, template, 'subtext');
  const showSalary = templateShowsField(declared, template, 'salary');
  const showBannerTitle = templateShowsField(
    declared,
    template,
    'banner_title'
  );
  const showBenefits = templateShowsField(declared, template, 'benefits');

  /*
   * SYM-5973 / SYM-5974. These two were the ONLY controls in this panel not asking the question
   * every other one asks, so the Accent picker was offered on the four templates that never draw
   * with it and the Brand picker on the three that never draw with it.
   *
   * Steven changed the accent on J5, watched nothing happen, and reported the picker as broken. It
   * was not broken. J5 has nothing for an accent colour to paint, and the panel had no way to say
   * so -- the same do-nothing input SYM-5875 removed for the hook line and SYM-5915 for the
   * callout, surviving in the two controls neither of those tickets touched.
   */
  const showBrandColour = templateShowsField(
    declared,
    template,
    'primary_color'
  );
  const showAccentColour = templateShowsField(
    declared,
    template,
    'accent_color'
  );

  // S1 and J1 draw none of these, so the heading would introduce an empty section.
  const hasAnyField = templateHasAnyField(declared, template);

  // SYM-6351 (X-5). The campaign's own Job title and Location, where this design draws them.
  const campaignFields: ValueField[] = [
    ...(templateShowsField(declared, template, 'job_title')
      ? (['title'] as ValueField[])
      : []),
    ...(templateShowsField(declared, template, 'location')
      ? (['location'] as ValueField[])
      : []),
  ];

  /*
   * SYM-6301. The logo row: which buttons, and which one is lit.
   *
   * The lit button is the customer's choice, or -- when nobody has clicked -- this template's own
   * place. That is why an untouched ad shows one lit button and why switching template moves the
   * lit button with the logo: both come from the template, not from the ad.
   */
  const overlay = defaultAdOverlays.find(
    (item) => item.template_code === template
  );
  const logoRow = templateLogoChoice(
    overlay?.logo_choice,
    overlay?.logo_place_default
  );
  const storedPlace = currentVariant?.logo_place ?? null;
  const litPlace = storedPlace ?? logoRow.ownPlace;
  // Two ways to have no logo, and the row says different things about them. No FILE means there
  // is nothing to place, so the buttons are shown but dead, with a line saying why (D9). The
  // customer switching it off is a choice, and the row stays live so they can switch it back.
  const hasLogoFile = !!currentVariant?.logo;

  /*
   * A click on the button that is ALREADY LIT stores nothing (D17).
   *
   * Without this, a curious customer who clicks the lit button on an untouched ad silently pins
   * every later template to that place -- nothing moves on screen, so there is no way to notice,
   * and switching template afterwards no longer follows the design.
   *
   * On the two-button templates, "Show logo" against a stored "none" is the ONE way back to "not
   * chosen" (D16b): it clears the choice rather than storing a place the customer never picked.
   */
  const pickPlace = (next: LogoPlace) => {
    if (next === litPlace) return;
    updateCurrentVariant('logo_place', next);
    refreshTemplatePreview();
  };

  const showLogo = () => {
    if (storedPlace !== 'none') return;
    updateCurrentVariant('logo_place', null);
    refreshTemplatePreview();
  };

  return (
    <div tw="flex flex-col gap-4">
      {hasAnyField && (
        <div>
          <Title3 tw="text-2xl font-bold">Template content</Title3>
          <p tw="text-sm text-gray-500">
            Position, size and colour contrast are handled for you. Leave a
            field empty to hide it.
          </p>
        </div>
      )}

      <CampaignValueRows fields={campaignFields} />

      {logoRow.row !== 'none' && (
        <div>
          <label tw="text-sm font-semibold">Logo</label>
          <div tw="flex flex-wrap gap-2 mt-1">
            {logoRow.row === 'places' ? (
              <LogoPlaceButtons
                value={litPlace}
                disabled={!hasLogoFile}
                onPick={pickPlace}
              />
            ) : (
              <>
                <LogoButton
                  label="Show logo"
                  lit={storedPlace !== 'none'}
                  disabled={!hasLogoFile}
                  onClick={showLogo}
                />
                <LogoButton
                  label="No logo"
                  lit={storedPlace === 'none'}
                  disabled={!hasLogoFile}
                  onClick={() => pickPlace('none')}
                />
              </>
            )}
          </div>
          {!hasLogoFile && (
            <p tw="text-sm text-gray-500 mt-1">This ad has no logo yet.</p>
          )}
        </div>
      )}

      {showBannerTitle && (
        <Field
          label="Callout"
          hint="The word on the badge, e.g. WANTED. Leave empty to hide the badge."
          value={currentVariant?.banner_title ?? ''}
          limit={MAX_LENGTHS.banner_title}
          onChange={(v) => setField('banner_title', v)}
        />
      )}

      {showHook && (
        <Field
          label="Hook line"
          hint="Shown above or below the job title, depending on the template."
          value={currentVariant?.free_text ?? ''}
          limit={MAX_LENGTHS.free_text}
          onChange={(v) => setField('free_text', v)}
        />
      )}

      {showExtra && (
        <Field
          label="Extra line"
          hint="A second short line, e.g. contract type or start date."
          value={currentVariant?.subtext ?? ''}
          limit={MAX_LENGTHS.subtext}
          onChange={(v) => setField('subtext', v)}
        />
      )}

      {showSalary && (
        <Field
          label="Salary"
          hint={
            canExposeSalary
              ? 'Shown in its own band above the benefits.'
              : 'Salary display is not enabled on this plan, so this will not appear on the ad.'
          }
          disabled={!canExposeSalary}
          value={currentVariant?.salary ?? ''}
          limit={MAX_LENGTHS.salary}
          onChange={(v) => setField('salary', v)}
        />
      )}

      {showBenefits && (
        <div tw="flex flex-col gap-2">
          <label tw="text-sm font-semibold">Benefits</label>
          {Array.from({ length: MAX_BENEFITS }).map((_, i) => (
            <Field
              key={i}
              label=""
              value={benefits[i] ?? ''}
              limit={MAX_LENGTHS.benefit}
              placeholder={`Benefit ${i + 1}`}
              onChange={(v) => {
                const next = [...benefits];
                next[i] = v;
                setBenefits(next);
              }}
            />
          ))}
          <p tw="text-xs text-gray-500">
            Up to {MAX_BENEFITS}. Empty lines are skipped.
          </p>
        </div>
      )}

      {/*
        SYM-5911. Mark: "enkel voor deze advertentie. De globale merkkleur van de klant aanpassen
        vanuit een ad-editor is verrassend en zou met terugwerkende kracht andere ads raken."
        So this writes the VARIANT, never the brand, exactly as the accent picker below does.

        It is also the only way a customer can correct a colour they did not choose. Until
        SYM-5916 a variant took its colours from the LOGO, so 2,498 production variants carry a
        hardcoded grey while their brand has a real colour -- and nothing in the product could
        change it. SYM-5916 fixes what NEW ads start with; this fixes the ones already made.

        AbstractTemplateRenderer::primary() reads the variant's copy and never the brand's, so
        this value is what every template is actually painted with.
      */}
      {showBrandColour && (
        <div tw="flex flex-col gap-2">
          <label tw="text-sm font-semibold">Brand colour</label>
          <ColorPicker
            customStyles={tw`w-full`}
            /*
             * SYM-5929. The RESOLVED colour, not the ad's own copy.
             *
             * This was `primary_color ?? '#000000'`, so an ad that had never been given a colour
             * showed black -- and black was not even what it would draw: the renderer used a #2b2b2b
             * grey, or the brand's colour once SYM-5929 landed. Three answers to one question, and
             * the customer saw the only one that was never true. Steven hit this in QA and could not
             * tell an unset colour from a deliberate black.
             *
             * The fallback chain lives on the SERVER and arrives resolved. Recomputing it here would
             * put one rule in two places, which is how SYM-5875, SYM-5908 and SYM-5912 each drifted.
             * The '#000000' at the end is now only reachable if the API sent nothing at all.
             */
            value={
              currentVariant?.resolved_primary_color ??
              currentVariant?.primary_color ??
              '#000000'
            }
            onChange={(color) => {
              updateCurrentVariant('primary_color', color);
              refreshTemplatePreview();
            }}
          />
          <p tw="text-xs text-gray-500">
            Used for the bars, panels and pills. Applies to this ad only.
          </p>
        </div>
      )}

      {showAccentColour && (
        <div tw="flex flex-col gap-2">
          <label tw="text-sm font-semibold">Accent colour</label>
          <ColorPicker
            customStyles={tw`w-full`}
            // SYM-5929: resolved by the server, same reasoning as the brand colour above.
            value={
              currentVariant?.resolved_accent_color ??
              currentVariant?.accent_color ??
              '#000000'
            }
            onChange={(color) => {
              updateCurrentVariant('accent_color', color);
              refreshTemplatePreview();
            }}
          />
          <p tw="text-xs text-gray-500">
            Used for the pill, badge or button. Text on it is contrasted
            automatically.
          </p>
        </div>
      )}
    </div>
  );
};

const Field = ({
  label,
  hint,
  value,
  limit,
  placeholder,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  limit: number;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) => {
  // SYM-5907: a hard cap. This was a warning, on the reasoning that "the renderer ellipsises
  // rather than overflowing, so a long value is survivable". The renderer did not ellipsise, so
  // the message promised something nothing delivered and the ad visibly broke instead.
  //
  // Only values ALREADY over the limit can be over it now, from rows saved before this shipped.
  // They are left alone rather than silently trimmed -- deleting a customer's words on page load
  // is worse than showing them a full counter -- so the guard below blocks growth, not existence,
  // and any change that makes the value shorter is always allowed.
  const overLimit = value.length > limit;

  const accept = (next: string) => {
    if (acceptsFieldEdit(next, value, limit)) {
      onChange(next);
    }
  };

  // The label used to be a bare <label> next to the input, which associates with nothing: clicking
  // it did not focus the field, a screen reader announced the input as unlabelled, and getByLabel
  // could not find it even while it was on screen holding a value. Input already forwards an id.
  //
  // uuid rather than React 18's useId because @types/react is pinned at 16 and does not declare it.
  // Input.tsx generates its own id the same way, so this is the house pattern rather than a new one.
  const fieldId = useMemo(() => uuid(), []);

  return (
    <div tw="flex flex-col gap-1">
      {label !== '' && (
        <label htmlFor={fieldId} tw="text-sm font-semibold">
          {label}
        </label>
      )}
      <Input
        id={fieldId}
        enableEmoji={false}
        inputStyles={tw`w-full`}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        // Native cap, so the browser also blocks paste and drag-and-drop. `accept` stays as the
        // real guard: maxLength is not applied to a programmatic value change, and it would trim
        // a legacy value down instead of leaving it be.
        maxLength={Math.max(limit, value.length)}
        onChange={({ target }) => accept(target.value)}
      />
      {disabled ? (
        hint && <p tw="text-xs text-gray-500">{hint}</p>
      ) : overLimit ? (
        <p tw="text-xs text-amber-600">
          {value.length}/{limit} characters. The ad cuts this to fit; shorten it
          to control where it ends.
        </p>
      ) : (
        <p tw="text-xs text-gray-500">
          {hint}
          {hint ? ' ' : ''}
          <span tw="text-gray-400">
            {value.length}/{limit}
          </span>
        </p>
      )}
    </div>
  );
};

export default TemplateValueControls;
